/**
 * Created by robert.lavoie on 6/20/17.
 */

import { Injectable, Injector, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { Observable ,  of } from 'rxjs';
import * as Raven from 'raven-js';
import { HttpClient, HttpParams } from '@angular/common/http';

// Environment Vars
import { environment } from '../../environments/environment';

// Models
import {
  getJobSearchSection,
  LogoPosition,
  Owner,
  SECTION_TYPE_CONTENT,
  SECTION_TYPE_JOB_SEARCH,
  Site,
} from 'careerpages-base/models';

/**
 *
 * @class
 * SiteService
 *
 * @description
 * This service is responsible for filtering and returning and any information regarding a Site
 *
 */
@Injectable()
export class SiteService {

  public site: Site;

  // the homepage path, used for routing - EX: /cups
  public homepagePath: string = '/';
  // this the domain name from the request, this is needed to get the site data - EX: /cups/about
  public currentPath: string;
  // EX: nowhiring.com/cups/about
  public currentUrl: string;
  // EX: nowhiring.com/cups
  public currentSiteUrl: string;
  // nowhiring.com
  public domainName: string;
  // the slug unique to the site, if applicable - e.g. "cups", will be undefined for custom domains
  public siteSlug: string;
  // www.nowhiring.com
  public host: string;
  // http or https
  public protocol: string;
  public apiUrl: string;
  // If there is no parameter that assume are looking for the home page
  // At this point we know there is a site
  public currentPageName: string = 'home';

  private http: HttpClient;

  constructor(
    private inject: Injector,
    private injector: Injector,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {


    // Get the current domain name depending on platform
    if (isPlatformBrowser(this.platformId)) {
      this.protocol = window.location.protocol.replace(':', '');
      this.host = window.location.host;
      this.currentPath = window.location.pathname;
    } else if (isPlatformServer(this.platformId)) {
      const req = this.injector.get(REQUEST);
      this.protocol = 'https';
      this.host = req.hostname;
      this.currentPath = req.originalUrl.split('?')[0];
    }

    this.apiUrl = `${this.protocol}://${this.host}/api`;

    // remove www for the site lookup
    this.domainName = this.host.replace('www.', '');

    // If under our internal domain or localhost we need to change the base domain
    if (this.domainName.startsWith('localhost') || this.domainName.startsWith('careerpages')) {
      // use edge api directly when not routing through a proxy
      if (this.domainName.startsWith('localhost')) {
        this.apiUrl = `${environment.NGINX_PROXY_URL}/api`;
      }
      // override domain name for search
      this.domainName = environment.NOW_HIRING_DOMAIN;
      let domainNameQueryString: string;

      // override the domain name if the cpdomain query string is set for internal testing
      if (isPlatformBrowser(this.platformId)) {
        domainNameQueryString = this.getUrlParameter('cpdomain', window.location.search.substring(1));
      } else if (isPlatformServer(this.platformId)) {
        const req = this.injector.get(REQUEST);
        domainNameQueryString = this.getUrlParameter('cpdomain', req.originalUrl);
      }

      if (domainNameQueryString !== null && domainNameQueryString !== '') {
        this.domainName = domainNameQueryString;
      }

    }

    // store parts of the currentPath into an array
    const paths: string[] = this.getUrlPaths();

    // Normalize the currentSiteUrl depending on domainName
    if (this.domainName === environment.NOW_HIRING_DOMAIN && paths[0]){
      this.currentSiteUrl = `${this.domainName}/${paths[0]}`;
      this.homepagePath = `/${paths[0]}`;
      this.siteSlug = paths[0];
    } else {
      this.currentSiteUrl = this.domainName;
    }

    this.currentUrl = this.host + this.currentPath;

    // Toggle this to pull data from a given environment
    // Make sure you disable CORS as this will throw warning
    // Recommended to use Safari with the "Disable CORS" setting enabled
    // this.apiUrl = `http://career-pages-edge.api.snagprod.corp`;

  }

  /**
   * Loads the customer site before the app loads
   *
   * @return { Promise: Site }
   */
  load(): Promise<any> {
    this.http = this.inject.get(HttpClient);

    return this.http
      .get(`${this.getApiUrl()}/career-live-sites/${encodeURIComponent(this.currentSiteUrl)}`)
      .toPromise()
      .then((data: Site) => {
        this.cleanSiteData(data);
        this.site = data;
        Promise.resolve();
      })
      .catch((err: any) => {
        Promise.resolve();
      });

  }

  /**
   * Handles cleaning up any site data we get back from the API
   * @param { Site } site - site to be cleaned
   */
  private cleanSiteData(site: Site) {
    this.cleanSectionTypes(site);
    this.ensureJobSearchSection(site);
    this.cleanLogoPosition(site);
    this.ensureScripts(site);
    this.themeColors(site);
  }

  /**
   * Ensures all sections have a type, defaulting to content
   * @param { Site } site - site data from the API
   */
  private cleanSectionTypes(site: Site) {
    site.pages.map((page) => {
      page.sections.filter(section => !section.type).map((section) => {
        section.type = SECTION_TYPE_CONTENT;
      });
    });
  }

  /**
   * Looks at a given site object to ensure it has *exactly* one job search section per page
   * Mutates the data by way of pass by reference
   * @param { Site } site - site data from the API
   */
  private ensureJobSearchSection(site: Site) {
    // Go through every page in the site
    site.pages.map((page) => {

      // If we have no job search sections, add one
      if (page.sections.filter(section => section.type === SECTION_TYPE_JOB_SEARCH).length === 0) {
        page.sections.push(getJobSearchSection());
      }
    });
  }

  /**
   * Ensures a logo position is set if it doesn't exist
   * @param { Site } site - site data from the API
   */
  private cleanLogoPosition(site: Site) {
    if (!site.theme.logoPosition) site.theme.logoPosition = LogoPosition.INTRO;
  }

  /**
   * Ensures the script element on a site is an array
   * @param { Site } site - site data from the API
   */
  private ensureScripts(site: Site) {
    if (!site.scripts) site.scripts = [];
  }

  /**
   * Return the customer site
   *
   * @return { object: Site }
   */
  getSite(): Site {
    return this.site;
  }


  /**
   * Return the owner details
   *
   * @return { Owner }
   */
  getOwner(): Owner{
    return this.site.owner;
  }

  /**
   * Return the owner id
   *
   * @return { string }
   */
  getOwnerId(): string{
    return this.site.owner.id;
  }


  /**
   * Returns the an array of values that match the provided key
   *
   * @param { string } - The fieldName to filter on
   * @return { string[] }
   */
  getSearchParams(type: string): string[]{

    const searchIds: string[] = [];
    this.site.jobSearchCriteria.map((searchParam) => {
      if (searchParam.fieldName.toLowerCase() === type.toLowerCase()){
        searchIds.push(searchParam.fieldValue);
      }
    });

    return searchIds;

  }

  /**
   * Return the customer site theme
   *
   * @return { string }
   */
  getSiteTheme(): string {
    return this.site.theme.color;
  }

  /**
   * themeColors ensure a default color is set to the theme options
   * @param site
   */
  themeColors(site: Site) {
    if (site.theme.buttonColor == null) site.theme.buttonColor = site.theme.color;
    if (site.theme.footerColor == null) site.theme.footerColor = site.theme.color;
    if (site.theme.headerBackgroundColor == null)site.theme.headerBackgroundColor = site.theme.color;
    if (site.theme.footerBackgroundColor == null) site.theme.footerBackgroundColor = site.theme.color;
    if (site.theme.headerColor == null) site.theme.headerColor = site.theme.color;
  }

  /**
   * Returns the current URL
   *
   * @return { string }
   */
  getCurrentUrl(): string {
    return this.protocol + '://' + this.currentUrl;
  }


  /**
   * Returns the an array url paths
   *
   * @return { string[] }
   */
  getUrlPaths(): string[] {
    return this.currentPath.split('/').filter((path) => { return path.trim() != ''; });
  }


  /**
   * Returns the domainName
   *
   * @return { string }
   */
  getDomainName(): string {
    return this.domainName;
  }

  /**
   * Returns the ApiUrl
   * api URLs requested via HTTP or HTTPS on the server must be absolute - EX http://localhost:3000
   *
   * @return { string }
   */
  getApiUrl(): string {
    return this.apiUrl;
  }



  /**
   * Returns the HomepagePath
   *
   * @return { string }
   */
  getHomepagePath(): string {
    return this.homepagePath;
  }


  /**
   * Returns the value of a querystring param
   *
   * @param { string }
   * @param { string }
   * @return { string }
   */
  getUrlParameter(name: string, url: string): string {
    const n = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + n + '=([^&#]*)');
    const results = regex.exec(url);
    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
  }


  /**
   * Handles the search errors
   *
   * @param { any }
   * @return { Observable }
   */
  private handleError(error: any): Observable<any> {
    Raven.captureException(error);
    return of(error);
  }

}
